// Preloads images into a cache by URL. Callers get the HTMLImageElement synchronously
// once preload() has completed. Missing images resolve to a transparent placeholder
// so a broken URL doesn't crash the game.

export interface PreloadProgress {
  loaded: number;
  total: number;
}

export class AssetLoader {
  private cache = new Map<string, HTMLImageElement>();
  private placeholder: HTMLImageElement;

  constructor() {
    // A 1x1 transparent image for fallback.
    const img = new Image();
    img.src =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=";
    this.placeholder = img;
  }

  get(url: string): HTMLImageElement {
    return this.cache.get(url) ?? this.placeholder;
  }

  has(url: string): boolean {
    return this.cache.has(url);
  }

  async preload(
    urls: readonly string[],
    onProgress?: (p: PreloadProgress) => void,
  ): Promise<void> {
    const unique = Array.from(new Set(urls));
    let loaded = 0;
    const total = unique.length;
    onProgress?.({ loaded: 0, total });
    await Promise.all(
      unique.map(
        (url) =>
          new Promise<void>((resolve) => {
            if (this.cache.has(url)) {
              loaded++;
              onProgress?.({ loaded, total });
              resolve();
              return;
            }
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
              this.cache.set(url, img);
              loaded++;
              onProgress?.({ loaded, total });
              resolve();
            };
            img.onerror = () => {
              // Don't fail the whole preload on one bad asset.
              console.warn(`[AssetLoader] failed to load ${url}`);
              loaded++;
              onProgress?.({ loaded, total });
              resolve();
            };
            img.src = url;
          }),
      ),
    );
  }
}
